import { useEffect, useState, useRef, RefObject } from 'react';

interface IntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * Custom hook to create an Intersection Observer
 */
export const useIntersectionObserver = (
  elementRef: RefObject<Element>,
  options: IntersectionObserverOptions = {
    threshold: 0,
    rootMargin: '0px',
  },
  onIntersect?: () => void
): boolean => {
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Create the observer instance
    observer.current = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        
        // Call the onIntersect callback when entering the viewport
        if (entry.isIntersecting && onIntersect) {
          onIntersect();
        }
      },
      {
        root: options.root || null,
        rootMargin: options.rootMargin || '0px',
        threshold: options.threshold || 0,
      }
    );

    // Start observing the target element
    const currentElement = elementRef.current;
    if (currentElement) {
      observer.current.observe(currentElement);
    }

    // Cleanup function
    return () => {
      if (observer.current && currentElement) {
        observer.current.unobserve(currentElement);
        observer.current.disconnect();
      }
    };
  }, [elementRef, options.root, options.rootMargin, options.threshold, onIntersect]);

  return isIntersecting;
};

export default useIntersectionObserver;